import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { editCoupon, createCoupon, getCoupons } from '../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Switch
} from '@mui/material'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'

const CREATE_COUPON = gql`${createCoupon}`
const EDIT_COUPON = gql`${editCoupon}`
const GET_COUPONS = gql`${getCoupons}`

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }
const labelSx = { fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }

function CouponForm(props) {
  const { t } = props
  const isEdit = !!props.coupon

  const [title, setTitle] = useState(props.coupon?.title || '')
  const [discount, setDiscount] = useState(props.coupon?.discount ?? '')
  const [enabled, setEnabled] = useState(props.coupon ? props.coupon.enabled : false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const mutation = isEdit ? EDIT_COUPON : CREATE_COUPON

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_COUPONS }],
    onCompleted: () => {
      const message = isEdit ? t('CouponUpdated') : t('CouponAdded')
      setSuccess(message)
      setError('')
      if (!isEdit) {
        setTitle('')
        setDiscount('')
        setEnabled(false)
      }
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: err => {
      let message = ''
      try { message = err.graphQLErrors[0].message } catch (e) { message = t('ActionFailedTryAgain') }
      setError(message)
      setSuccess('')
      setTimeout(() => setError(''), 3000)
    }
  })

  const handleSave = () => {
    if (!title.trim()) {
      setError('Coupon code is required')
      setTimeout(() => setError(''), 3000)
      return
    }
    if (discount === '' || isNaN(Number(discount)) || Number(discount) < 1 || Number(discount) > 99) {
      setError('Discount must be between 1 and 99')
      setTimeout(() => setError(''), 3000)
      return
    }
    mutate({
      variables: {
        couponInput: {
          _id: props.coupon?._id || '',
          title: title.trim(),
          discount: +discount,
          enabled
        }
      }
    })
  }

  return (
    <Box>
      {/* Header banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #431407 60%, #c2410c 100%)', borderRadius: '14px', px: 2.5, py: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LocalOfferIcon sx={{ color: '#fdba74', fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
            {isEdit ? t('EditCoupon') : t('AddCoupon')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{enabled ? 'Active' : 'Inactive'}</Typography>
          <Switch
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            size="small"
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }}
          />
        </Box>
      </Box>

      {/* Fields */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={labelSx}>{t('Code')}</Typography>
          <TextField
            fullWidth size="small"
            placeholder={t('PHCode') || 'e.g. SALE50'}
            value={title}
            onChange={e => setTitle(e.target.value.toUpperCase())}
            sx={fieldSx}
            inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.05em', fontWeight: 700 } }}
          />
        </Box>
        <Box>
          <Typography sx={labelSx}>{t('Discount')} (%)</Typography>
          <TextField
            fullWidth size="small" type="number"
            placeholder={t('PHDiscount') || 'e.g. 20'}
            value={discount}
            onChange={e => {
              const v = e.target.value.replace(/[^0-9]/g, '')
              if (v === '' || (+v >= 1 && +v <= 99)) setDiscount(v)
            }}
            inputProps={{ min: 1, max: 99 }}
            sx={fieldSx}
          />
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
        {props.onClose && (
          <Button variant="outlined" onClick={props.onClose}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3, color: '#64748b', borderColor: '#e2e8f0', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          disabled={loading}
          onClick={handleSave}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, background: 'linear-gradient(135deg, #f97316, #ea580c)', boxShadow: '0 4px 12px rgba(249,115,22,0.35)', '&:hover': { background: 'linear-gradient(135deg, #ea580c, #c2410c)' } }}>
          {loading ? 'Saving...' : t('Save')}
        </Button>
      </Box>
    </Box>
  )
}

export default withTranslation()(CouponForm)